import React from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';
import { dashboardService } from '../../services/dashboardService';
import { APP_ROUTES } from '../../routes/routingContract';
import { percentFormatter } from '../../utils/formatters';
import MarkdownRenderer from '../common/MarkdownRenderer';
import EmbedRenderer from '../chat/embeds/EmbedRenderer';
import StateBlock from '../common/StateBlock';
import Skeleton from '../common/Skeleton/Skeleton';
import { getUserFacingError } from '../../lib/userFacingErrors';

const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(Number(value) || 0);
const formatPercent = percentFormatter(0, 0);
const RANGE_OPTIONS = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12m', label: '12 months' },
];
const AI_WIDGET_STORAGE_KEY = 'wealthy-dashboard-ai-widget';
const DEFAULT_WIDGET_SIZE = { width: 440, height: 620 };
const DEFAULT_WIDGET_POSITION = { top: 84, right: 20 };
const TOP_TRANSACTIONS_PAGE_SIZE = 5;
const SMART_CHIPS = [
  'Why is my income high?',
  'What drove spending?',
  'Where can I cut back?',
  'How much did I save?',
];

function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function KpiStrip({ summary }) {
  const kpis = summary?.kpis || {};
  const savingsRate = kpis.income > 0 ? kpis.net_savings / kpis.income : 0;
  const items = [
    { label: 'Net worth', value: formatCurrency(kpis.net_worth) },
    { label: 'Income', value: formatCurrency(kpis.income) },
    { label: 'Spending', value: formatCurrency(kpis.spending) },
    { label: 'Savings rate', value: formatPercent(savingsRate) },
  ];

  return (
    <div className="dashboard-kpis">
      {items.map((item) => (
        <div className="dashboard-kpi" key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

function CashFlowWaterfall({ cashFlow }) {
  const steps = cashFlow?.waterfall || [];
  const maxValue = Math.max(...steps.map((step) => Math.abs(toNumber(step.value))), 1);
  const income = toNumber(cashFlow?.income);
  const netSavings = toNumber(cashFlow?.net_savings);
  const savingsTone = netSavings >= 0 ? 'positive' : 'negative';
  const summary = income > 0
    ? `You ${netSavings >= 0 ? 'kept' : 'spent beyond income by'} ${formatCurrency(Math.abs(netSavings))} of ${formatCurrency(income)} income this period.`
    : 'Cash-flow detail will appear after income and spending history is available.';

  return (
    <section className="dashboard-panel dashboard-panel--wide" aria-labelledby="cash-flow-title">
      <div className="dashboard-panel__header">
        <div>
          <h2 id="cash-flow-title">Income to savings</h2>
        </div>
      </div>
      <p className={`dashboard-chart-takeaway dashboard-chart-takeaway--${savingsTone}`}>{summary}</p>
      <div className="waterfall" data-testid="cash-flow-waterfall-track" aria-label="Income to net savings waterfall">
        {steps.map((step, index) => {
          const width = `${(Math.abs(toNumber(step.value)) / maxValue) * 100}%`;
          const isNegative = toNumber(step.value) < 0;
          return (
            <React.Fragment key={step.id}>
              <div className="waterfall__step">
                <div className="waterfall__value">
                  <strong className={isNegative ? 'dashboard-negative' : ''}>
                    {isNegative ? '-' : ''}{formatCurrency(Math.abs(toNumber(step.value)))}
                  </strong>
                  <span>{step.label}</span>
                </div>
                <div className="waterfall__bar-track" aria-hidden="true">
                  <div
                    className={`waterfall__bar waterfall__bar--${step.id}${isNegative ? ' waterfall__bar--negative' : ''}`}
                    style={{ '--bar-width': width }}
                  />
                </div>
              </div>
              {index < steps.length - 1 && <span className="waterfall__connector" aria-hidden="true" />}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}

function NetWorthBreakdown({ netWorth }) {
  const breakdown = netWorth?.breakdown || [];
  const maxValue = Math.max(...breakdown.map((item) => Math.abs(toNumber(item.value))), 1);
  const assets = toNumber(netWorth?.assets);
  const liabilities = toNumber(netWorth?.liabilities);
  const compositionMax = Math.max(assets, liabilities, 1);

  return (
    <section className="dashboard-panel" aria-labelledby="net-worth-title">
      <div className="dashboard-panel__header">
        <div>
          <h2 id="net-worth-title">Your net worth</h2>
        </div>
      </div>
      <div className="net-worth-total">
        <span>Net worth</span>
        <strong>{formatCurrency(netWorth?.net_worth)}</strong>
      </div>
      <div className="net-worth-composition" data-testid="net-worth-composition" aria-label="Assets and liabilities totals">
        <div className="net-worth-composition__column">
          <div className="net-worth-composition__summary">
            <span>Assets</span>
            <strong>{formatCurrency(assets)}</strong>
          </div>
          <div className="net-worth-composition__track" aria-hidden="true">
            <div
              className="net-worth-composition__fill net-worth-composition__fill--asset"
              style={{ '--fill-width': `${(assets / compositionMax) * 100}%` }}
            />
          </div>
        </div>
        <div className="net-worth-composition__column">
          <div className="net-worth-composition__summary">
            <span>Liabilities</span>
            <strong className={liabilities > 0 ? 'dashboard-negative' : ''}>
              {liabilities > 0 ? '-' : ''}{formatCurrency(Math.abs(liabilities))}
            </strong>
          </div>
          <div className="net-worth-composition__track" aria-hidden="true">
            <div
              className="net-worth-composition__fill net-worth-composition__fill--liability"
              style={{ '--fill-width': `${(liabilities / compositionMax) * 100}%` }}
            />
          </div>
        </div>
      </div>
      <div className="breakdown-list">
        {breakdown.length === 0 ? (
          <p className="dashboard-muted">Connect accounts to see your net worth breakdown.</p>
        ) : breakdown.map((item) => (
          <div className="breakdown-row" key={item.id}>
            <span>{item.label}</span>
            <div className="breakdown-track" aria-hidden="true">
              <div
                className={`breakdown-fill breakdown-fill--${item.kind}`}
                style={{ '--fill-width': `${(Math.abs(toNumber(item.value)) / maxValue) * 100}%` }}
              />
            </div>
            <strong>{item.kind === 'liability' ? '-' : ''}{formatCurrency(Math.abs(toNumber(item.value)))}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function SpendingCategories({ spending }) {
  const categories = spending?.categories || [];
  const maxAmount = Math.max(...categories.map((item) => toNumber(item.amount)), 1);

  return (
    <section className="dashboard-panel" aria-labelledby="spending-title">
      <div className="dashboard-panel__header">
        <div>
          <h2 id="spending-title">Where your money went</h2>
        </div>
      </div>
      <div className="category-list">
        {categories.length === 0 ? (
          <p className="dashboard-muted">No spending transactions found for this range.</p>
        ) : categories.map((category) => {
          return (
            <div
              className="category-row"
              key={category.id}
              data-testid={`spending-category-${category.id}`}
            >
              <div
                className="category-row__summary"
                tabIndex={0}
              >
                <div className="category-row__text">
                  <span>{category.label}</span>
                  <small>{category.transaction_count} transactions</small>
                </div>
                <div className="category-track" aria-hidden="true">
                  <div style={{ '--fill-width': `${(toNumber(category.amount) / maxAmount) * 100}%` }} />
                </div>
                <strong>{formatCurrency(category.amount)}</strong>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function formatShortDate(value) {
  if (!value) return '';
  return new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function TopTransactionsPanel({ spending }) {
  const transactions = spending?.top_transactions || [];
  const [page, setPage] = React.useState(0);
  const pageCount = Math.max(1, Math.ceil(transactions.length / TOP_TRANSACTIONS_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleTransactions = transactions.slice(
    currentPage * TOP_TRANSACTIONS_PAGE_SIZE,
    currentPage * TOP_TRANSACTIONS_PAGE_SIZE + TOP_TRANSACTIONS_PAGE_SIZE
  );
  const hasPagination = transactions.length > TOP_TRANSACTIONS_PAGE_SIZE;

  React.useEffect(() => {
    setPage(0);
  }, [transactions.length]);

  return (
    <section className="dashboard-panel dashboard-panel--wide" aria-labelledby="top-transactions-title">
      <div className="dashboard-panel__header">
        <div>
          <h2 id="top-transactions-title">Largest transactions</h2>
        </div>
      </div>
      <div className="top-transactions" aria-label="Top spending transactions">
        {transactions.length === 0 ? (
          <p className="dashboard-muted">Largest transactions will appear after spending history is available.</p>
        ) : visibleTransactions.map((transaction) => (
          <div className="top-transaction" key={transaction.id || `${transaction.date}-${transaction.name}`}>
            <div className="top-transaction__merchant">
              <strong>{transaction.merchant_name || transaction.name}</strong>
              <small>{transaction.category_label || 'Unknown'} · {transaction.account_name || 'Account'} · {formatShortDate(transaction.date)}</small>
            </div>
            <strong>{formatCurrency(transaction.amount)}</strong>
          </div>
        ))}
      </div>
      {hasPagination && (
        <div className="top-transactions-pagination" aria-label="Top transactions pagination">
          <button
            type="button"
            className="top-transactions-pagination__button"
            disabled={currentPage === 0}
            aria-label="Previous transactions page"
            onClick={() => setPage((value) => Math.max(0, value - 1))}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span>{currentPage + 1} / {pageCount}</span>
          <button
            type="button"
            className="top-transactions-pagination__button"
            disabled={currentPage >= pageCount - 1}
            aria-label="Next transactions page"
            onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </section>
  );
}

function DashboardAiWidget({ open, range, onClose, onMinimize, minimized, onRestore }) {
  const [messages, setMessages] = React.useState([
    {
      role: 'assistant',
      content: 'Ask anything about your money, spending, savings, income, or net worth.',
      embeds: [],
    },
  ]);
  const [draft, setDraft] = React.useState('');
  const [streaming, setStreaming] = React.useState(false);
  const [panelState, setPanelState] = React.useState(() => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(AI_WIDGET_STORAGE_KEY) || 'null');
      if (stored?.size && stored?.position) return stored;
    } catch (_) {}
    return { size: DEFAULT_WIDGET_SIZE, position: DEFAULT_WIDGET_POSITION };
  });
  const [dragState, setDragState] = React.useState(null);
  const [resizeState, setResizeState] = React.useState(null);
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const maxLeft = window.innerWidth - 320;
    const currentLeft = panelState.position.left ?? (window.innerWidth - panelState.size.width - panelState.position.right);
    if (currentLeft > maxLeft || currentLeft < 0 || panelState.position.top < 0) {
      setPanelState({ size: DEFAULT_WIDGET_SIZE, position: DEFAULT_WIDGET_POSITION });
    }
  }, [open, panelState.position, panelState.size]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(AI_WIDGET_STORAGE_KEY, JSON.stringify(panelState));
  }, [panelState]);

  React.useEffect(() => {
    if (!dragState && !resizeState) return undefined;

    const handleMouseMove = (event) => {
      if (dragState) {
        const width = panelState.size.width;
        const height = panelState.size.height;
        const left = Math.min(Math.max(8, event.clientX - dragState.offsetX), window.innerWidth - width - 8);
        const top = Math.min(Math.max(8, event.clientY - dragState.offsetY), window.innerHeight - height - 8);
        setPanelState((current) => ({ ...current, position: { left, top } }));
      }

      if (resizeState) {
        const width = Math.min(Math.max(360, resizeState.width + event.clientX - resizeState.startX), window.innerWidth - 24);
        const height = Math.min(Math.max(360, resizeState.height + event.clientY - resizeState.startY), window.innerHeight - 48);
        setPanelState((current) => ({ ...current, size: { width, height } }));
      }
    };
    const stop = () => {
      setDragState(null);
      setResizeState(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stop);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stop);
    };
  }, [dragState, resizeState, panelState.size]);

  if (!open) return null;
  if (minimized) {
    return (
      <button type="button" className="dashboard-ai-minimized" onClick={onRestore} data-testid="dashboard-ai-minimized">
        <span>Dashboard chat</span>
        <small>{messages.length} message{messages.length === 1 ? '' : 's'}</small>
      </button>
    );
  }

  const sendMessage = async (event, overrideText) => {
    event?.preventDefault();
    const text = (overrideText || draft).trim();
    if (!text || streaming) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setDraft('');
    setStreaming(true);
    let assistantText = '';
    let assistantEmbeds = [];
    const assistantMessage = { role: 'assistant', content: '', embeds: [], isStreaming: true };
    setMessages([...nextMessages, assistantMessage]);

    try {
      const apiHistory = nextMessages.map((message) => ({
        role: message.role,
        content: message.content,
      }));

      await dashboardService.streamDashboardAsk(
        { message: text, range, history: apiHistory },
        {
          onTextDelta: (delta) => {
            assistantText += delta;
            setMessages([...nextMessages, { role: 'assistant', content: assistantText, embeds: assistantEmbeds, isStreaming: true }]);
          },
          onEmbedComplete: (embed) => {
            assistantEmbeds = [...assistantEmbeds, embed];
            setMessages([...nextMessages, { role: 'assistant', content: assistantText, embeds: assistantEmbeds, isStreaming: true }]);
          },
          onDone: (data) => {
            setMessages([...nextMessages, { role: 'assistant', content: data.response || assistantText, embeds: data.embeds || assistantEmbeds }]);
          },
          onError: (error) => {
            const friendlyError = getUserFacingError(error, 'dashboard-ai');
            setMessages([...nextMessages, { role: 'assistant', content: friendlyError.message, embeds: [], error: friendlyError }]);
          },
        }
      );
    } catch (error) {
      const friendlyError = getUserFacingError(error, 'dashboard-ai');
      setMessages([...nextMessages, { role: 'assistant', content: friendlyError.message, embeds: [], error: friendlyError }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <aside
      ref={panelRef}
      className="dashboard-ai-widget"
      data-testid="dashboard-ai-widget"
      aria-label="Ask about your money"
      style={{
        '--widget-width': `${panelState.size.width}px`,
        '--widget-height': `${panelState.size.height}px`,
        '--widget-left': panelState.position.left != null ? `${panelState.position.left}px` : 'auto',
        '--widget-right': panelState.position.left != null ? 'auto' : `${panelState.position.right}px`,
        '--widget-top': `${panelState.position.top}px`,
      }}
    >
      <header
        className="dashboard-ai-widget__header"
        onMouseDown={(event) => {
          if (event.target.closest('button')) return;
          const rect = panelRef.current?.getBoundingClientRect();
          if (!rect) return;
          setDragState({ offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top });
        }}
      >
        <div>
          <h2>Ask about your money</h2>
          <p>Spending, savings, income, and net worth.</p>
        </div>
        <div className="dashboard-ai-widget__controls">
          <button type="button" onClick={onMinimize} aria-label="Minimize dashboard chat">−</button>
          <button type="button" onClick={onClose} aria-label="Close dashboard chat">×</button>
        </div>
      </header>
      <div className="dashboard-ai-messages">
        {messages.map((message, idx) => (
          <div key={`${message.role}-${idx}`} className={`dashboard-ai-message dashboard-ai-message--${message.role}`}>
            {message.content ? <MarkdownRenderer content={message.content} /> : (message.isStreaming ? 'Thinking...' : '')}
            {message.embeds && Array.isArray(message.embeds) && <EmbedRenderer embeds={message.embeds} />}
          </div>
        ))}
      </div>
      <div className="dashboard-ai-suggestions" aria-label="Suggested dashboard questions">
        {SMART_CHIPS.map((suggestion) => (
          <button type="button" key={suggestion} onClick={() => sendMessage(null, suggestion)} disabled={streaming}>{suggestion}</button>
        ))}
      </div>
      <form className="dashboard-ai-form" data-testid="dashboard-ai-form" onSubmit={sendMessage}>
        <textarea
          data-testid="dashboard-ai-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Ask anything about your money..."
          aria-label="Ask a question about your money"
          rows={1}
        />
        <button
          type="submit"
          data-testid="dashboard-ai-submit"
          disabled={streaming || !draft.trim()}
          aria-label="Send message"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 13V3M3 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </form>
      <button
        type="button"
        className="dashboard-ai-resize"
        aria-label="Resize dashboard chat"
        onMouseDown={(event) => {
          event.preventDefault();
          setResizeState({
            startX: event.clientX,
            startY: event.clientY,
            width: panelState.size.width,
            height: panelState.size.height,
          });
        }}
      />
    </aside>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [range, setRange] = React.useState('30d');
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [aiOpen, setAiOpen] = React.useState(false);
  const [aiMinimized, setAiMinimized] = React.useState(false);
  const [headerControlsScrolling, setHeaderControlsScrolling] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    dashboardService.getSummary(range)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) setError(getUserFacingError(err, 'dashboard-load'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range, refreshKey]);

  const noAccounts = !loading && !error && (summary?.coverage?.connected_accounts || 0) === 0;
  const collapseHeaderControls = headerControlsScrolling || (aiOpen && !aiMinimized);

  React.useEffect(() => {
    let scrollTimer;

    const handleScroll = () => {
      setHeaderControlsScrolling(true);
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(() => {
        setHeaderControlsScrolling(false);
      }, 650);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.clearTimeout(scrollTimer);
    };
  }, []);

  return (
    <section className="dashboard-page" aria-label="Money dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header__title">
          <h1>Your money dashboard</h1>
        </div>
        <div className={`dashboard-header__actions ${collapseHeaderControls ? 'dashboard-header__actions--collapsed' : ''}`}>
          <label className="dashboard-range-control">
            <span className="visually-hidden">Dashboard range</span>
            <select value={range} onChange={(event) => setRange(event.target.value)} aria-label="Dashboard range">
              {RANGE_OPTIONS.map((option) => (
                <option value={option.value} key={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="dashboard-ask-button"
            data-testid="dashboard-ask-button"
            aria-label="Ask about your money"
            onClick={() => {
              setAiOpen(true);
              setAiMinimized(false);
            }}
          >
            <span className="dashboard-ask-button__desktop">Ask about your money</span>
            <span className="dashboard-ask-button__mobile" aria-hidden="true">Ask</span>
          </button>
        </div>
      </header>

      {loading && <DashboardSkeleton />}
      {error && (
        <StateBlock
          state="error"
          title={error.title}
          message={error.message}
          actionLabel={error.actionLabel || 'Retry'}
          onAction={() => setRefreshKey((value) => value + 1)}
        />
      )}
      {noAccounts && (
        <DashboardEmptyState onConnect={() => navigate(APP_ROUTES.accounts)} />
      )}

      {!loading && !error && summary && !noAccounts && (
        <>
          <KpiStrip summary={summary} />
          <div className="dashboard-grid">
            <CashFlowWaterfall cashFlow={summary.cash_flow} />
            <NetWorthBreakdown netWorth={summary.net_worth} />
            <SpendingCategories spending={summary.spending} />
            <TopTransactionsPanel spending={summary.spending} />
          </div>
        </>
      )}

      <DashboardAiWidget
        open={aiOpen}
        minimized={aiMinimized}
        range={range}
        onClose={() => setAiOpen(false)}
        onMinimize={() => setAiMinimized(true)}
        onRestore={() => setAiMinimized(false)}
      />
    </section>
  );
}

function DashboardEmptyState({ onConnect }) {
  return (
    <section className="dashboard-empty-hero" aria-labelledby="dashboard-empty-title">
      <div className="dashboard-empty-hero__copy">
        <div className="dashboard-empty-hero__icon" aria-hidden="true">
          <span />
        </div>
        <h2 id="dashboard-empty-title">Connect accounts to build your dashboard</h2>
        <p>
          Link bank, credit, investment, and loan accounts so Wealth-Y can turn your real balances and transactions into a clear money dashboard.
        </p>
        <div className="dashboard-empty-hero__checks" aria-label="What connected accounts unlock">
          <span>Cash flow</span>
          <span>Net worth</span>
          <span>Spending patterns</span>
        </div>
        <button type="button" onClick={onConnect}>
          Connect accounts
        </button>
      </div>

      <div className="dashboard-empty-preview" aria-hidden="true">
        <div className="dashboard-empty-preview__header">
          <span />
          <span />
        </div>
        <div className="dashboard-empty-preview__kpis">
          <div>
            <span>Cash flow</span>
            <strong>$0</strong>
          </div>
          <div>
            <span>Net worth</span>
            <strong>$0</strong>
          </div>
        </div>
        <div className="dashboard-empty-preview__chart">
          <span style={{ '--bar-height': '42%' }} />
          <span style={{ '--bar-height': '66%' }} />
          <span style={{ '--bar-height': '54%' }} />
          <span style={{ '--bar-height': '78%' }} />
          <span style={{ '--bar-height': '62%' }} />
        </div>
        <div className="dashboard-empty-preview__rows">
          <span />
          <span />
          <span />
        </div>
      </div>
    </section>
  );
}

function DashboardSkeleton() {
  return (
    <div className="dashboard-skeleton" aria-label="Loading dashboard">
      <div className="dashboard-kpis">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div className="dashboard-kpi-card" key={`dashboard-kpi-skeleton-${idx}`}>
            <Skeleton width="45%" height="12px" />
            <Skeleton width="70%" height="28px" />
            <Skeleton width="90%" height="14px" />
          </div>
        ))}
      </div>
      <div className="dashboard-grid">
        {Array.from({ length: 4 }).map((_, idx) => (
          <section className="dashboard-panel" key={`dashboard-panel-skeleton-${idx}`}>
            <Skeleton width="35%" height="18px" />
            <Skeleton width="100%" height="180px" variant="rectangular" />
          </section>
        ))}
      </div>
    </div>
  );
}
